import React, { useState } from 'react';
import BookingModal from './components/BookingModal';
import Calculator from './components/Calculator';
import Testimonials from './components/Testimonials';
import CostOfInaction from './components/CostOfInaction';
import Roadmaps from './components/Roadmaps';
import Features from './components/Features';
import { categories, totalProgramValue, totalCoursesCount } from './data/courses';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const App = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-amber-500 selection:text-black pb-20 md:pb-0">
      
      {/* 1. HERO SECTION */}
      <header className="relative pt-20 pb-24 px-6 md:px-12 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[120px]"></div>
          <div className="absolute top-40 -left-20 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-[100px]"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-slate-900/80 border border-amber-500/30 rounded-full px-4 py-1.5 mb-8 shadow-xl backdrop-blur-md">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            <span className="text-xs md:text-sm font-bold text-amber-100 uppercase tracking-widest">
              Elite Business Acquisition
            </span>
          </div>
          
          <h1 className="text-4xl md:text-7xl font-extrabold text-white leading-[1.1] mb-6 tracking-tight">
            The Complete AI Business <br className="hidden md:block"/> Growth OS to Build Your <br className="hidden md:block"/> First <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-500 to-amber-200">₹1 Crore</span>
          </h1>
          
          <p className="text-lg md:text-2xl text-slate-400 mb-10 max-w-4xl mx-auto leading-relaxed font-light">
            <strong>{totalCoursesCount} programs + DFY workshops</strong> by field experts that install your lead-gen, sales funnel, and automation engine.
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-5 mb-12">
            <button 
              onClick={openModal}
              className="w-full md:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white text-xl font-black py-5 px-10 rounded-2xl shadow-[0_20px_50px_rgba(245,158,11,0.2)] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3"
            >
              <SafeIcon icon={FiIcons.FiPhoneCall} className="text-2xl" />
              Book A Call to Unlock Discount
            </button>
          </div>

          <Features />

          <div className="flex flex-col items-center gap-4">
             <div className="flex items-center gap-3 text-slate-400">
               <span className="line-through opacity-50 text-xl">₹2,50,000</span>
               <span className="text-white font-black text-3xl">₹1,00,000</span>
             </div>
             <p className="text-amber-500 text-xs font-bold uppercase tracking-widest bg-amber-950/20 px-4 py-2 rounded-lg border border-amber-500/20">
               *Limited: Discount unlocked only via strategy call
             </p>
          </div>
        </div>
      </header>

      {/* 2. COST OF INACTION */}
      <CostOfInaction />

      {/* 3. PROBLEM AGITATION */}
      <section className="py-24 px-6 bg-slate-900/50 border-y border-slate-800">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Why 97% of Coaches & Founders <br className="hidden md:block"/> Stay Stuck Under ₹1L/Month
            </h2>
            <p className="text-slate-400 text-lg">It's not about working harder. It's about the "Implementation Gap".</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              { q: "The Information Overload", a: "You have 50 tabs open, 10 unfinished courses, and no clear sequence. You're busy but not productive." },
              { q: "The Tech Nightmare", a: "Funnels don't talk to CRMs. WhatsApp isn't automated. You waste hours doing manual follow-ups." },
              { q: "The Content Wheel", a: "You post daily but get 'likes' instead of 'leads'. You have no automated lead distribution system." },
              { q: "The AI Gap", a: "Your competitors are using AI to do 1 month of work in 1 day. You're still doing it the 2019 way." }
            ].map((item, i) => (
              <div key={i} className="bg-slate-950/50 p-8 rounded-2xl border border-slate-800 group hover:border-amber-500/30 transition-all">
                <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-3">
                  <SafeIcon icon={FiIcons.FiXCircle} className="text-red-500" />
                  {item.q}
                </h4>
                <p className="text-slate-400 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. SOLUTION REFRAME */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              Diamond 2.0: Your <span className="text-amber-500">Business OS</span>
            </h2>
            <p className="text-slate-400 text-xl max-w-3xl mx-auto font-light">
              Stop buying "courses". Start installing the infrastructure that generates cash flow on autopilot.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((cat, idx) => (
              <div key={idx} className="bg-slate-900 border border-slate-800 p-8 rounded-3xl hover:bg-slate-800/50 transition-all cursor-default group">
                <div className="w-14 h-14 bg-amber-500/10 rounded-2xl flex items-center justify-center mb-6 text-amber-500 group-hover:bg-amber-500 group-hover:text-black transition-all">
                  <span className="text-2xl font-black">{idx + 1}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 leading-tight">{cat.title.split(': ')[1]}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{cat.description}</p>
                <div className="text-[10px] font-bold text-amber-500 uppercase tracking-widest opacity-60">Value: {cat.totalValue}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. VALUE STACK */}
      <section className="py-24 px-6 bg-slate-900/40">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">The Massive Value Stack</h2>
            <p className="text-slate-400">Everything you need, in one elite bundle.</p>
          </div>

          <div className="space-y-4">
            {categories.map((cat) => (
              <div key={cat.id} className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl">
                <button 
                  onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
                  className="w-full flex items-center justify-between p-7 text-left hover:bg-slate-900 transition-all"
                >
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-white">{cat.title}</h3>
                    <div className="flex items-center gap-4 mt-2">
                       <span className="text-xs text-amber-500 font-bold uppercase tracking-widest">{cat.courses.length} Programs</span>
                       <span className="text-xs text-slate-500">Worth {cat.totalValue}</span>
                    </div>
                  </div>
                  <SafeIcon 
                    icon={activeCategory === cat.id ? FiIcons.FiMinus : FiIcons.FiPlus} 
                    className={`text-xl transition-all ${activeCategory === cat.id ? 'text-amber-500' : 'text-slate-500'}`}
                  />
                </button>
                
                {activeCategory === cat.id && (
                  <div className="p-7 pt-0 border-t border-slate-800 bg-slate-900/30">
                    <div className="grid gap-3 mt-6">
                      {cat.courses.map((course, idx) => (
                        <div key={idx} className="flex items-center justify-between py-3 px-4 bg-slate-800/30 rounded-lg border border-slate-800/50">
                          <span className="text-slate-300 font-medium text-sm">{course.title}</span>
                          <span className="text-[10px] font-mono text-slate-500">₹{(course.price * 83).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-16 bg-gradient-to-br from-slate-800 to-slate-950 p-10 rounded-[2rem] border border-amber-500/20 text-center shadow-2xl">
            <h4 className="text-slate-400 text-sm font-bold uppercase tracking-widest mb-2">Total OS Value</h4>
            <div className="text-5xl md:text-7xl font-black text-white mb-4">{totalProgramValue}</div>
            <p className="text-xl text-slate-400 mb-10 max-w-xl mx-auto font-light">
              You are not buying courses. You are buying <strong>Speed, Clarity & Execution</strong>.
            </p>
            <button 
              onClick={openModal}
              className="bg-amber-500 hover:bg-amber-400 text-black font-black py-5 px-12 rounded-2xl transition-all transform hover:scale-105 shadow-xl shadow-amber-500/10"
            >
              Apply To Unlock Discount
            </button>
          </div>
        </div>
      </section>

      {/* 6. ROI CALCULATOR */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <Calculator />
        </div>
      </section>

      {/* 7. ROADMAPS */}
      <Roadmaps />

      {/* 8. TESTIMONIALS */}
      <Testimonials />

      {/* 9. FAQ */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
           <h2 className="text-4xl font-black text-white mb-12 text-center">Final Objections</h2>
           <div className="space-y-4">
             {[
               { q: "Is this for Tamil audience only?", a: "The framework works worldwide. The teaching is in Simple English for maximum clarity." },
               { q: "I'm not tech-savvy. Can I do this?", a: "That is exactly why we have the 'DFY Workshops' (Done-For-You). We show you where to click." },
               { q: "How do I unlock the discounted price?", a: "Click any button on this page to book a call. We review your business and unlock the discount based on your commitment." },
               { q: "Do I get support?", a: "This is a self-paced OS library. Strategy calls and implementation support are available as premium upgrades." }
             ].map((faq, i) => (
               <details key={i} className="bg-slate-900/50 rounded-2xl border border-slate-800 open:border-amber-500/20 transition-all">
                 <summary className="p-7 cursor-pointer font-bold text-white flex justify-between items-center list-none">
                   {faq.q}
                   <SafeIcon icon={FiIcons.FiChevronDown} className="text-slate-500" />
                 </summary>
                 <div className="px-7 pb-7 text-slate-400 text-sm leading-relaxed border-t border-slate-800/50 pt-5">
                   {faq.a}
                 </div>
               </details>
             ))}
           </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section className="py-32 px-6 bg-gradient-to-b from-slate-950 to-slate-900 overflow-hidden relative">
        <div className="absolute inset-0 bg-amber-500/5 blur-[150px] rounded-full"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-7xl font-black text-white mb-8">Ready to Install Your <br/> Business OS?</h2>
          <p className="text-xl text-slate-400 mb-12 font-light">This is a closed-room elite bundle. We review applications to ensure we only work with action-takers.</p>
          
          <button 
            onClick={openModal}
            className="group relative inline-flex items-center justify-center"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
            <span className="relative bg-amber-500 hover:bg-amber-400 text-black text-2xl font-black py-6 px-16 rounded-2xl transition-all flex items-center gap-4">
              Book A Call & Unlock Deal
              <SafeIcon icon={FiIcons.FiArrowRight} />
            </span>
          </button>
          
          <p className="mt-8 text-slate-500 text-sm">Application takes 60 seconds. Selective admission.</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-16 px-6 border-t border-slate-900 bg-slate-950 text-center">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8 text-slate-600 text-xs">
          <div className="text-left">
            <h5 className="text-white font-bold mb-2">Diamond 2.0 Business OS</h5>
            <p>&copy; {new Date().getFullYear()} All rights reserved. Results not typical.</p>
          </div>
          <div className="flex gap-8">
            <a href="#" className="hover:text-white transition-colors">Privacy</a>
            <a href="#" className="hover:text-white transition-colors">Terms</a>
            <a href="#" className="hover:text-white transition-colors">Contact</a>
          </div>
        </div>
      </footer>

      {/* STICKY CTA (Mobile & Desktop) */}
      <div className="fixed bottom-6 right-6 z-50 group hidden md:block">
        <button 
          onClick={openModal}
          className="bg-amber-500 hover:bg-amber-400 text-black font-black px-8 py-4 rounded-full shadow-[0_20px_50px_rgba(245,158,11,0.4)] transition-all flex items-center gap-3 transform hover:scale-105"
        >
          <SafeIcon icon={FiIcons.FiPhoneCall} className="animate-bounce" />
          Book Call - Unlock {totalCoursesCount} Programs
        </button>
      </div>

      <div className="fixed bottom-0 left-0 w-full p-4 md:hidden z-50 pointer-events-none">
        <button 
          onClick={openModal}
          className="w-full bg-amber-500 text-black font-black py-4 rounded-xl shadow-2xl shadow-amber-500/20 pointer-events-auto active:scale-95 transition-all flex items-center justify-center gap-2"
        >
          <SafeIcon icon={FiIcons.FiPhoneCall} />
          Book A Call Now
        </button>
      </div>

      <BookingModal isOpen={isModalOpen} onClose={closeModal} />
    </div>
  );
};

export default App;